import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ExploreIcon from "@mui/icons-material/Explore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import { resolveLandingAssetUrl } from "../../../utils/landingAssets";
import { buildDefaultDesignTokens } from "./config";
import LandingSegmentToolbar from "../landingStudio/LandingSegmentToolbar";
import {
  SEGMENT_LABELS,
  normalizeImagePosition,
  normalizeVisualStyle,
  segmentCssVariables,
  segmentVisualScale,
  visualStyleFromTemplate,
} from "../landingStudio/segmentVisualStyle";
import { getSegmentDefinition } from "../landingStudio/segmentRegistry";
import { youtubeEmbedUrl } from "./youtube";
import { normalizeCalendlyUrl } from "../../../utils/calendly";
import { applyLandingSegmentSnapshot, getLandingSegments } from "./landingLayout";
import {
  getLandingPreviewDevice,
  resolveLandingPreviewResponsiveValue,
} from "../landingStudio/previewDevices";

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

const HERO_CTA_ICON_COMPONENTS = Object.freeze({
  explore: ExploreIcon,
  storefront: StorefrontIcon,
  whatsapp: WhatsAppIcon,
  phone: PhoneIcon,
  mail: EmailIcon,
  place: PlaceIcon,
  shopping: ShoppingBagIcon,
  event: EventAvailableIcon,
  star: StarIcon,
  verified: VerifiedIcon,
  autoawesome: AutoAwesomeIcon,
  arrow: ArrowForwardIcon,
});

function HeroCanvasCtaIcon({ name = "", fallback = "explore" }) {
  const Icon = HERO_CTA_ICON_COMPONENTS[String(name || "").trim()] || HERO_CTA_ICON_COMPONENTS[fallback];
  return Icon ? <Icon /> : null;
}

const HIGHLIGHT_ICON_COMPONENTS = Object.freeze({
  star: StarRoundedIcon,
  support: SupportAgentRoundedIcon,
  verified: VerifiedRoundedIcon,
  speed: BoltRoundedIcon,
  quality: WorkspacePremiumRoundedIcon,
  local: StorefrontRoundedIcon,
  security: ShieldRoundedIcon,
  autoawesome: AutoAwesomeRoundedIcon,
  event: EventAvailableRoundedIcon,
  groups: GroupsRoundedIcon,
  photo: PhotoCameraRoundedIcon,
  design: DesignServicesRoundedIcon,
});

function HighlightCanvasIcon({ name = "", ...props }) {
  const Icon = HIGHLIGHT_ICON_COMPONENTS[String(name || "").trim()];
  if (!Icon) return null;
  return <Icon {...props} />;
}

function SafeCanvasImage({ src, alt = "", sx, onEdit, editLabel = "Editar imagen" }) {
  const [broken, setBroken] = React.useState(false);

  React.useEffect(() => {
    setBroken(false);
  }, [src]);

  if (!src || broken) return null;

  const handleKeyDown = (event) => {
    if (!onEdit || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    onEdit(event);
  };

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      role={onEdit ? "button" : undefined}
      tabIndex={onEdit ? 0 : undefined}
      title={onEdit ? editLabel : undefined}
      aria-label={onEdit ? editLabel : undefined}
      onClick={onEdit}
      onKeyDown={handleKeyDown}
      onError={() => setBroken(true)}
      sx={{
        ...sx,
        ...(onEdit ? {
          cursor: "pointer",
          outline: "2px solid transparent",
          outlineOffset: -2,
          transition: "outline-color .15s ease, filter .15s ease",
          "&:hover": {
            outlineColor: "rgba(243,90,58,.92)",
            filter: "brightness(.96)",
          },
          "&:focus-visible": {
            outlineColor: "rgba(243,90,58,1)",
          },
        } : {}),
      }}
    />
  );
}

function mergeDesignTokens(theme, source) {
  const defaults = buildDefaultDesignTokens(theme);
  const tokens = isPlainObject(source) ? source : {};
  const merged = Object.fromEntries(
    Object.keys(defaults).map((group) => [
      group,
      {
        ...defaults[group],
        ...(isPlainObject(tokens[group]) ? tokens[group] : {}),
      },
    ])
  );
  const themeColors = {
    primary: theme?.primaryColor,
    secondary: theme?.secondaryColor,
    background: theme?.backgroundColor,
    surface: theme?.surfaceColor,
    text: theme?.textColor,
    mutedText: theme?.mutedTextColor,
    divider: theme?.dividerColor,
  };
  merged.colors = {
    ...merged.colors,
    ...Object.fromEntries(Object.entries(themeColors).filter(([, value]) => String(value || "").trim())),
  };
  return merged;
}

function finite(value, fallback) {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function px(value, fallback = 0) {
  return `${finite(value, fallback)}px`;
}

function normalizeHeroHorizontalPosition(value) {
  const candidate = String(value || "").trim().toLowerCase();
  if (["right", "derecha", "end", "flex-end"].includes(candidate)) return "right";
  if (["center", "centre", "centro"].includes(candidate)) return "center";
  return "left";
}

function normalizeHeroVerticalPosition(value) {
  const candidate = String(value || "").trim().toLowerCase();
  if (["top", "arriba", "start", "flex-start"].includes(candidate)) return "top";
  if (["bottom", "abajo", "end", "flex-end"].includes(candidate)) return "bottom";
  return "center";
}

function heroHorizontalFlex(value) {
  if (value === "right") return "flex-end";
  if (value === "center") return "center";
  return "flex-start";
}

function heroVerticalFlex(value) {
  if (value === "top") return "flex-start";
  if (value === "bottom") return "flex-end";
  return "center";
}

function colorWithAlpha(value, alphaHex) {
  const candidate = String(value || "").trim();
  if (!candidate || candidate.toLowerCase() === "transparent") return "transparent";
  if (/^#[0-9a-f]{6}$/i.test(candidate)) return `${candidate}${alphaHex}`;
  if (/^#[0-9a-f]{3}$/i.test(candidate)) {
    const expanded = `#${candidate.slice(1).split("").map((char) => `${char}${char}`).join("")}`;
    return `${expanded}${alphaHex}`;
  }
  return candidate;
}

function SectionFrame({
  sectionId,
  sectionType = sectionId,
  sectionLabel = "",
  activeSection,
  studioMode,
  onSelectSection,
  onOpenSegmentTool,
  onToggleSection,
  device,
  children,
  sx,
  backgroundSx,
  layoutOrder,
}) {
  const active = activeSection === sectionId;
  const label = sectionLabel || SEGMENT_LABELS[sectionType] || SEGMENT_LABELS[sectionId] || sectionId;
  const definition = getSegmentDefinition(sectionType);
  const canToggle = sectionType === "custom" || Boolean(definition?.visibilityPath);
  const select = () => onSelectSection?.(sectionId);

  return (
    <Box data-landing-section-frame={sectionId} sx={Number.isFinite(layoutOrder) ? { order: layoutOrder } : undefined}>
      {studioMode && active ? (
        <LandingSegmentToolbar
          sectionId={sectionId}
          label={label}
          enabled
          device={device}
          canToggle={canToggle}
          onToggle={(nextVisible) => onToggleSection?.(sectionId, nextVisible)}
          onOpenTool={onOpenSegmentTool}
        />
      ) : null}

      <Box
        component="section"
        data-landing-section={sectionId}
        data-landing-section-active={active ? "true" : undefined}
        aria-label={studioMode ? `${label}. Tocá para editar esta sección.` : undefined}
        onClick={select}
        sx={{
          position: "relative",
          scrollMarginBlock: "72px",
          cursor: studioMode ? "pointer" : "default",
          touchAction: studioMode ? "manipulation" : undefined,
          WebkitTapHighlightColor: studioMode ? "transparent" : undefined,
          outline: active ? "3px solid rgba(243,90,58,.88)" : "2px solid transparent",
          outlineOffset: -2,
          boxShadow: active ? "inset 0 0 0 1px rgba(255,255,255,.72)" : undefined,
          transition: "outline-color .15s ease, box-shadow .15s ease",
          "&:hover": studioMode ? { outlineColor: "rgba(243,90,58,.48)" } : undefined,
          ...sx,
          ...backgroundSx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function hexColorWithOpacity(value, opacity, fallback) {
  const color = String(value || fallback || "#ffffff").trim();
  const alpha = Math.max(0, Math.min(1, finite(opacity, 1)));
  const match = color.match(/^#([0-9a-f]{6})$/i);
  if (!match) return color;
  const hex = match[1];
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function PlaceholderCards({ labels, primary, surface, tokens, device, cardConfig = {}, imageScale = 1, spacingScale = 1 }) {
  const showImage = cardConfig.showImage !== false;
  const showTitle = cardConfig.showTitle !== false;
  const showDuration = cardConfig.showDuration !== false;
  const showCapabilities = cardConfig.showCapabilities !== false;
  const imagePosition = ["top", "left", "right"].includes(cardConfig.imagePosition) ? cardConfig.imagePosition : "top";
  const horizontalImage = showImage && imagePosition !== "top" && device !== "mobile";
  const cardBackgroundImage = resolveLandingAssetUrl(cardConfig.backgroundImageUrl);
  const backgroundImagePosition = normalizeImagePosition(cardConfig.backgroundImagePosition);
  const backgroundImageFit = cardConfig.backgroundImageFit === "contain" ? "contain" : "cover";
  const cardRadius = finite(cardConfig.radius, tokens.borders.radiusMd);
  const cardGap = finite(cardConfig.gap, 18);
  const cardImageHeight = finite(cardConfig.imageHeight, 210) * imageScale;
  const showType = cardConfig.showType !== false;
  const showCategory = cardConfig.showCategory !== false;
  const showSubcategory = cardConfig.showSubcategory === true;
  const showDescription = cardConfig.showDescription !== false;
  const showPrice = cardConfig.showPrice !== false;
  const showButton = cardConfig.showButton !== false;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: resolveLandingPreviewResponsiveValue(
          device,
          { xs: "1fr", sm: "repeat(3, 1fr)" },
          "1fr"
        ),
        gap: `${cardGap}px`,
        mt: 2,
      }}
    >
      {labels.map((label, index) => (
        <Paper
          key={`${label}-${index}`}
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: horizontalImage ? (imagePosition === "right" ? "row-reverse" : "row") : "column",
            borderRadius: `${cardRadius}px`,
            bgcolor: hexColorWithOpacity(cardConfig.backgroundColor, cardConfig.backgroundOpacity, surface),
            border: `${finite(tokens.borders.width, 1)}px ${tokens.borders.style || "solid"} ${tokens.borders.color || tokens.colors.divider}`,
            boxShadow: tokens.shadows.sm,
            isolation: "isolate",
            "&::before": cardBackgroundImage ? {
              content: '""',
              position: "absolute",
              inset: 0,
              zIndex: 0,
              pointerEvents: "none",
              backgroundImage: `url("${String(cardBackgroundImage).replace(/"/g, "%22")}")`,
              backgroundSize: backgroundImageFit,
              backgroundPosition: backgroundImagePosition,
              backgroundRepeat: "no-repeat",
              opacity: Math.max(0, Math.min(1, finite(cardConfig.backgroundImageOpacity, 0.28))),
            } : undefined,
            "& > *": { position: "relative", zIndex: 1 },
          }}
        >
          {showImage ? (
            <Box
              sx={{
                width: horizontalImage ? "38%" : "100%",
                minWidth: horizontalImage ? 72 : 0,
                height: horizontalImage ? "auto" : `${cardImageHeight}px`,
                minHeight: horizontalImage ? `${Math.min(cardImageHeight, 150)}px` : 0,
                bgcolor: colorWithAlpha(primary, "18"),
                background: `linear-gradient(135deg, ${colorWithAlpha(primary, "22")}, ${colorWithAlpha(tokens.colors.secondary, "18")})`,
                flexShrink: 0,
              }}
            />
          ) : null}
          <Stack sx={{ p: px(finite(tokens.spacing.md, 16) * spacingScale, 16), minWidth: 0, flex: 1 }} spacing={0.45}>
            {showType ? <Typography variant="caption" sx={{ color: primary, fontWeight: 800 }}>{index % 2 ? "Servicio" : "Producto"}</Typography> : null}
            {showTitle ? <Typography sx={{ fontSize: ".88rem", fontWeight: finite(tokens.typography.fontWeightBold, 800) }}>{label}</Typography> : null}
            {showCategory ? <Typography variant="caption" sx={{ color: tokens.colors.mutedText }}>Categoría ejemplo</Typography> : null}
            {showSubcategory ? <Typography variant="caption" sx={{ color: tokens.colors.mutedText }}>Subcategoría</Typography> : null}
            {showDescription ? <Typography variant="caption" sx={{ color: tokens.colors.mutedText }}>Descripción visible según la configuración.</Typography> : null}
            {showDuration && index % 2 ? <Typography variant="caption" sx={{ color: tokens.colors.mutedText }}>Duración: 60 min</Typography> : null}
            {showCapabilities && index % 2 ? (
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                <Chip size="small" label="Reserva online" />
                <Chip size="small" label="Entrega" />
              </Stack>
            ) : null}
            {showPrice ? <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 900, color: tokens.colors.text }}>$ 12.500</Typography> : null}
            {showButton ? (
              <Button size="small" variant="contained" sx={{ alignSelf: "flex-start", mt: 0.7, textTransform: "none" }}>
                {index % 2 ? (cardConfig.serviceCtaLabel || "Reservar") : (cardConfig.productCtaLabel || "Ver detalle")}
              </Button>
            ) : null}
          </Stack>
        </Paper>
      ))}
    </Box>
  );
}

export default function LandingCanvas({
  form = {},
  activeSection = "",
  device = "desktop",
  productsEnabled = true,
  onSelectSection,
  studioMode = false,
  onOpenSegmentTool,
  onToggleSection,
  onlySection = "",
  bare = false,
  layoutInstanceId = "",
}) {
  const business = form.business || {};
  const navigation = form.navigation || {};
  const hero = form.hero || {};
  const catalog = form.catalog || {};
  const catalogCards = catalog.cards || {};
  const about = form.about || {};
  const highlightsIntro = form.highlightsIntro || {};
  const highlights = Array.isArray(form.highlights) ? form.highlights : [];
  const contact = form.contact || {};
  const custom = form.custom || {};
  const footer = form.footer || {};
  const sections = form.sections || {};
  const theme = form.theme || {};
  const layoutSegments = getLandingSegments(form);
  const segmentAllowed = (type) => onlySection
    ? onlySection === type
    : layoutSegments.some((segment) => segment.id === type);
  const orderFor = (type) => {
    const targetId = layoutInstanceId || type;
    const index = layoutSegments.findIndex((segment) => segment.id === targetId);
    return index >= 0 ? index : layoutSegments.length;
  };
  const sectionBackgroundSx = (type) => {
    const targetId = layoutInstanceId || type;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === type);
    const imageUrl = resolveLandingAssetUrl(segment?.visualStyle?.backgroundImageUrl);
    if (!imageUrl) return {};
    return {
      backgroundImage: `url("${String(imageUrl).replace(/"/g, "%22")}")`,
      backgroundSize: "cover",
      backgroundPosition: normalizeImagePosition(segment?.visualStyle?.backgroundImagePosition),
      backgroundRepeat: "no-repeat",
    };
  };
  const tokens = mergeDesignTokens(theme, form.designTokens);
  const colors = tokens.colors;
  const paletteForSection = (sectionKey) => {
    const targetId = sectionKey === "custom" && layoutInstanceId ? layoutInstanceId : sectionKey;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === sectionKey);
    return segment?.visualStyle?.useGlobalColors === false
      ? { ...colors, ...(segment.visualStyle.colors || {}) }
      : colors;
  };
  const elementColorSxForSection = (sectionKey) => {
    const targetId = sectionKey === "custom" && layoutInstanceId ? layoutInstanceId : sectionKey;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === sectionKey);
    if (segment?.visualStyle?.useGlobalColors !== false) return {};
    const elementColors = segment?.visualStyle?.elementColors;
    if (!elementColors || typeof elementColors !== "object") return {};
    return Object.entries(elementColors).reduce((sx, [path, rawStyle]) => {
      if (!path || !rawStyle || typeof rawStyle !== "object") return sx;
      const safePath = String(path).replace(/["\\]/g, "\\$&");
      const selector = `& [data-landing-field="${safePath}"]`;
      const normal = {};
      const hover = {};
      if (rawStyle.color) normal.color = `${rawStyle.color} !important`;
      if (rawStyle.background) normal.backgroundColor = `${rawStyle.background} !important`;
      if (rawStyle.borderColor) normal.borderColor = `${rawStyle.borderColor} !important`;
      if (rawStyle.shadow) {
        normal.boxShadow = `0 8px 24px ${rawStyle.shadow} !important`;
        normal.textShadow = `0 2px 10px ${rawStyle.shadow} !important`;
      }
      if (rawStyle.hoverColor) hover.color = `${rawStyle.hoverColor} !important`;
      if (rawStyle.hoverBackground) hover.backgroundColor = `${rawStyle.hoverBackground} !important`;
      if (Object.keys(normal).length) sx[selector] = normal;
      if (Object.keys(hover).length) sx[`${selector}:hover`] = hover;
      return sx;
    }, {});
  };
  const navbarColors = paletteForSection("navbar");
  const heroColors = paletteForSection("hero");
  const spotlightColors = paletteForSection("spotlight");
  const featuredColors = paletteForSection("featured");
  const brandsColors = paletteForSection("brands");
  const categoriesColors = paletteForSection("categories");
  const aboutColors = paletteForSection("about");
  const highlightsColors = paletteForSection("highlights");
  const customColors = paletteForSection("custom");
  const contactColors = paletteForSection("contact");
  const footerColors = paletteForSection("footer");
  const typography = tokens.typography;
  const spacing = tokens.spacing;
  const borders = tokens.borders;
  const shadows = tokens.shadows;
  const previewDevice = getLandingPreviewDevice(device);
  const responsive = (values, fallback) =>
    resolveLandingPreviewResponsiveValue(previewDevice.id, values, fallback);
  const desktopLayout = previewDevice.id === "desktop";
  const mobileLayout = previewDevice.id === "mobile";

  const heroHorizontalPosition = normalizeHeroHorizontalPosition(
    hero.contentHorizontalAlign ??
      hero.contentHorizontalPosition ??
      hero.textHorizontalPosition ??
      hero.contentPositionHorizontal ??
      hero.horizontalPosition
  );
  const heroVerticalPosition = normalizeHeroVerticalPosition(
    hero.contentVerticalAlign ??
      hero.contentVerticalPosition ??
      hero.textVerticalPosition ??
      hero.contentPositionVertical ??
      hero.verticalPosition
  );
  const heroContentJustify = heroHorizontalFlex(heroHorizontalPosition);
  const heroContentAlign = heroVerticalFlex(heroVerticalPosition);
  const heroTextAlign = heroHorizontalPosition;

  const primary = colors.primary;
  const secondary = colors.secondary;
  const surface = colors.surface;
  const background = colors.background;
  const text = colors.text;
  const muted = colors.mutedText;
  const onPrimary = colors.onPrimary;
  const onSecondary = colors.onSecondary;
  const horizontalPadding = responsive(
    { xs: px(spacing.md, 16), md: px(spacing.xxl, 48) },
    px(spacing.md, 16)
  );
  const cardBorder = `${finite(borders.width, 1)}px ${borders.style || "solid"} ${borders.color || colors.divider}`;
  const sectionAppearance = (sectionKey, current, palette) => {
    const targetId = sectionKey === "custom" && layoutInstanceId ? layoutInstanceId : sectionKey;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === sectionKey);
    if (segment?.visualStyle?.useGlobalColors !== false) return current || {};
    return {
      ...(current || {}),
      backgroundColor: palette.background,
      surfaceColor: palette.surface,
      accentColor: palette.primary,
      titleColor: palette.secondary,
      textColor: palette.mutedText || palette.text,
    };
  };
  const aboutAppearance = sectionAppearance("about", form.appearance?.about, aboutColors);
  const highlightsAppearance = sectionAppearance("highlights", form.appearance?.highlights, highlightsColors);
  const contactAppearance = sectionAppearance("contact", form.appearance?.contact, contactColors);
  const heroSlideIndex = Array.isArray(hero.slides)
    ? hero.slides.findIndex((slide) => slide?.url)
    : -1;
  const heroSlide = heroSlideIndex >= 0 ? hero.slides[heroSlideIndex] : null;
  const heroImageSourcePath = heroSlide
    ? `hero.slides.${heroSlideIndex}.url`
    : "hero.imageUrl";
  const heroImage = resolveLandingAssetUrl(heroSlide?.url || hero.imageUrl);
  const heroTitle = heroSlide?.title ?? hero.title ?? business.name ?? "";
  const heroSubtitle = heroSlide?.subtitle ?? hero.subtitle ?? "";
  const heroImagePosition = normalizeImagePosition(
    heroSlide?.position,
    normalizeImagePosition(hero.imagePosition)
  );
  const heroTitleFieldPath = heroSlide ? `hero.slides.${heroSlideIndex}.title` : "hero.title";
  const heroSubtitleFieldPath = heroSlide ? `hero.slides.${heroSlideIndex}.subtitle` : "hero.subtitle";
  const showHeroPrimaryCta = Boolean(hero.primaryCtaLabel && hero.primaryCtaUrl) &&
    (!hero.primaryCtaRequiresProducts || productsEnabled);
  const showHeroSecondaryCta = Boolean(hero.secondaryCtaLabel && hero.secondaryCtaUrl) &&
    (!hero.secondaryCtaRequiresProducts || productsEnabled);
  const showAboutCta = Boolean(about.ctaLabel && about.ctaUrl) &&
    (!about.ctaRequiresProducts || productsEnabled);
  const configuredBrands = Array.isArray(catalog.brands?.items)
    ? catalog.brands.items
        .map((item, sourceIndex) => ({ item, sourceIndex }))
        .filter(({ item }) => (item?.name ?? item?.label) || item?.logoUrl)
        .slice(0, 6)
    : [];
  const logoSourcePath = theme.logoUrl ? "theme.logoUrl" : "theme.compactLogoUrl";
  const logo = resolveLandingAssetUrl(theme.logoUrl || theme.compactLogoUrl);
  const navbarLogo = navigation.showLogo !== false ? logo : "";
  const aboutImage = resolveLandingAssetUrl(about.imageUrl);
  const highlightsBackground = resolveLandingAssetUrl(highlightsAppearance.backgroundImageUrl);
  const highlightsBackgroundPosition = normalizeImagePosition(highlightsAppearance.backgroundImagePosition);
  const highlightsBackgroundOpacity = Math.max(0, Math.min(1, finite(highlightsAppearance.backgroundImageOpacity, 0.24)));
  const contactImage = resolveLandingAssetUrl(contact.backgroundImageUrl);
  const contactImagePosition = normalizeImagePosition(contact.backgroundImagePosition);
  const spotlightLogo = resolveLandingAssetUrl(catalog.spotlight?.logoUrl);
  const spotlightBackground = resolveLandingAssetUrl(catalog.spotlight?.backgroundImageUrl);
  const spotlightBackgroundPosition = normalizeImagePosition(catalog.spotlight?.backgroundImagePosition);
  const footerCopyright = String(footer.copyright ?? "")
    .replace(/\{businessName\}/g, business.name ?? "")
    .replace(/\{year\}/g, String(new Date().getFullYear()));
  const navbarBusinessInfo = business.phone || business.email || business.address || "";
  const navbarButtons = Array.isArray(navigation.buttons)
    ? navigation.buttons
        .filter((item) => String(item?.label || "").trim())
        .slice(0, 8)
    : [];
  const heroHasContent = Boolean(
    heroImage ||
    hero.eyebrow ||
    heroTitle ||
    heroSubtitle ||
    (Array.isArray(hero.rotatingPhrases) && hero.rotatingPhrases.some(Boolean)) ||
    showHeroPrimaryCta ||
    showHeroSecondaryCta
  );
  const showSpotlightCta = Boolean(
    catalog.spotlight?.ctaLabel &&
    catalog.spotlight?.ctaUrl &&
    (!catalog.spotlight?.ctaRequiresProducts || productsEnabled)
  );
  const spotlightHasContent = Boolean(
    spotlightLogo ||
    catalog.spotlight?.eyebrow ||
    catalog.spotlight?.title ||
    catalog.spotlight?.description ||
    showSpotlightCta
  );
  const aboutHasCopy = Boolean(about.eyebrow || about.title || about.description || showAboutCta);
  const aboutHasContent = Boolean(aboutImage || aboutHasCopy);
  const visibleHighlights = highlights.filter((item) => item?.title || item?.description || item?.imageUrl || item?.youtubeUrl);
  const highlightsHasContent = Boolean(
    highlightsIntro.eyebrow ||
    highlightsIntro.title ||
    highlightsIntro.subtitle ||
    visibleHighlights.length
  );
  const businessAddress = [business.address, business.city, business.province, business.country]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(", ");
  const whatsappDigits = String(business.whatsapp || "").replace(/\D+/g, "");
  const contactHasDetails = Boolean(
    businessAddress || business.schedule || business.phone || business.email
  );
  const contactHasActions = Boolean(
    (whatsappDigits && contact.whatsappCtaLabel) ||
    (contact.detailsCtaLabel && contact.detailsCtaUrl) ||
    contactHasDetails
  );
  const contactHasContent = Boolean(
    contact.title || contact.subtitle || contactHasActions || contactImage
  );
  const footerHasBrand = Boolean((footer.showLogo !== false && logo) || business.name || footer.tagline);
  const footerSectionLinkVisible = (url, hasTarget) => {
    const target = String(url || "").trim();
    return !target.startsWith("#") || hasTarget;
  };
  const footerAboutVisible =
    sections.showAbout !== false &&
    navigation.showAboutLink !== false &&
    footerSectionLinkVisible(navigation.aboutUrl || "#about", aboutHasContent);
  const footerHighlightsVisible =
    sections.showHighlights !== false &&
    navigation.showHighlightsLink !== false &&
    footerSectionLinkVisible(navigation.highlightsUrl || "#highlights", highlightsHasContent);
  const footerContactVisible =
    sections.showContact !== false &&
    navigation.showContactLink !== false &&
    footerSectionLinkVisible(navigation.contactUrl || "/contact", contactHasContent);
  const footerHasNavigation = Boolean(
    footer.navigationTitle ||
    footer.homeLabel ||
    (productsEnabled && navigation.showProductsLink !== false && footer.productsLabel) ||
    (footerAboutVisible && navigation.aboutLabel) ||
    (footerHighlightsVisible && navigation.highlightsLabel) ||
    (footerContactVisible && footer.contactLabel)
  );
  const footerHasContact = Boolean(
    businessAddress || business.schedule || business.email || business.phone
  );
  const footerSocialLabels = navigation.showSocialLinks === false
    ? []
    : [
        whatsappDigits ? "WhatsApp" : "",
        business.socials?.instagram ? "Instagram" : "",
        business.socials?.facebook ? "Facebook" : "",
        business.socials?.youtube ? "YouTube" : "",
        business.socials?.linkedin ? "LinkedIn" : "",
        business.socials?.tiktok ? "TikTok" : "",
        business.socials?.website ? "Sitio web" : "",
      ].filter(Boolean);
  const footerHasVisibleContent = Boolean(
    footerHasBrand ||
    footerHasNavigation ||
    footerHasContact ||
    footerSocialLabels.length ||
    footerCopyright
  );
  const width = previewDevice.width;
  const scaleFor = (sectionId, kind) => segmentVisualScale(theme, sectionId, kind);
  const navbarStyle = normalizeVisualStyle(
    navigation.style,
    visualStyleFromTemplate(theme.template)
  );
  const footerStyle = normalizeVisualStyle(
    footer.style,
    visualStyleFromTemplate(theme.template)
  );
  const footerLogoPosition = normalizeImagePosition(footer.logoPosition);
  const navbarDark = navbarStyle === "style-3";
  const navbarCommercial = navbarStyle === "style-2";
  const navbarSpacingScale = scaleFor("navbar", "spacing");
  const navbarImageScale = scaleFor("navbar", "image");
  const navbarTitleScale = scaleFor("navbar", "title");
  const heroSpacingScale = scaleFor("hero", "spacing");
  const heroImageScale = scaleFor("hero", "image");
  const heroTitleScale = scaleFor("hero", "title");
  const spotlightSpacingScale = scaleFor("spotlight", "spacing");
  const spotlightImageScale = scaleFor("spotlight", "image");
  const spotlightTitleScale = scaleFor("spotlight", "title");
  const featuredSpacingScale = scaleFor("featured", "spacing");
  const featuredImageScale = scaleFor("featured", "image");
  const featuredTitleScale = scaleFor("featured", "title");
  const brandsSpacingScale = scaleFor("brands", "spacing");
  const brandsImageScale = scaleFor("brands", "image");
  const brandsTitleScale = scaleFor("brands", "title");
  const categoriesSpacingScale = scaleFor("categories", "spacing");
  const categoriesTitleScale = scaleFor("categories", "title");
  const aboutSpacingScale = scaleFor("about", "spacing");
  const aboutImageScale = scaleFor("about", "image");
  const aboutTitleScale = scaleFor("about", "title");
  const highlightsSpacingScale = scaleFor("highlights", "spacing");
  const highlightsImageScale = scaleFor("highlights", "image");
  const highlightsTitleScale = scaleFor("highlights", "title");
  const contactSpacingScale = scaleFor("contact", "spacing");
  const contactImageScale = scaleFor("contact", "image");
  const contactTitleScale = scaleFor("contact", "title");
  const footerSpacingScale = scaleFor("footer", "spacing");
  const footerImageScale = scaleFor("footer", "image");
  const footerTitleScale = scaleFor("footer", "title");

  const openImageEditor = (sectionId, imagePath) => (event) => {
    if (!studioMode || !onOpenSegmentTool) return;
    event?.stopPropagation?.();
    onSelectSection?.(sectionId);
    onOpenSegmentTool(sectionId, "image", imagePath || "");
  };

  const editableImageProps = (sectionId, imagePath, editLabel) => (
    studioMode && onOpenSegmentTool
      ? { onEdit: openImageEditor(sectionId, imagePath), editLabel }
      : {}
  );

  return (
    <Box
      data-landing-preview-device={previewDevice.id}
      data-landing-preview-width={width}
      sx={{
        width: bare ? "auto" : `${width}px`,
        maxWidth: bare ? undefined : "none",
        minHeight: "100%",
        bgcolor: background,
        color: text,
        overflow: "hidden",
        position: "relative",
        display: bare ? "contents" : "flex",
        flexDirection: bare ? undefined : "column",
        boxShadow: mobileLayout ? shadows.lg : "none",
        borderRadius: mobileLayout ? px(borders.radiusLg, 18) : 0,
        fontFamily: typography.bodyFontFamily || typography.fontFamily || "Asap, sans-serif",
        fontSize: px(typography.baseFontSize, 16),
        lineHeight: finite(typography.lineHeight, 1.5),
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          fontFamily: typography.headingFontFamily || typography.fontFamily || "inherit",
          lineHeight: finite(typography.headingLineHeight, 1.15),
          letterSpacing: px(typography.letterSpacing, 0),
        },
        "& .MuiButton-root": {
          borderRadius: px(borders.radiusPill, 9999),
          fontWeight: finite(typography.fontWeightMedium, 600),
        },
      }}
    >
      {segmentAllowed("navbar") && sections.showNavbar !== false ? (
        <SectionFrame
          sectionId="navbar"
          layoutOrder={orderFor("navbar")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("navbar")}
          sx={{ ...segmentCssVariables(theme, "navbar"), ...elementColorSxForSection("navbar") }}
        >
          {navbarCommercial && navbarBusinessInfo ? (
            <Box sx={{ px: horizontalPadding, py: 0.55 * navbarSpacingScale, bgcolor: navbarColors.secondary, color: navbarColors.onSecondary }}>
              <Typography data-landing-field="business.phone" variant="caption" sx={{ opacity: 0.82 }}>
                {navbarBusinessInfo}
              </Typography>
            </Box>
          ) : null}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{
              px: horizontalPadding,
              py: px(finite(spacing.md, 16) * navbarSpacingScale, 16),
              bgcolor: Object.keys(sectionBackgroundSx("navbar")).length ? "transparent" : (navbarDark ? secondary : surface),
              color: navbarDark ? navbarColors.onSecondary : navbarColors.text,
              borderBottom: cardBorder,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              {navbarLogo ? (
                <SafeCanvasImage
                  src={navbarLogo}
                  alt=""
                  sx={{ width: 38 * navbarImageScale, height: 38 * navbarImageScale, objectFit: "contain" }}
                  {...editableImageProps("navbar", logoSourcePath, "Editar logo del encabezado")}
                />
              ) : business.name ? (
                <Typography
                  data-landing-field="business.name"
                  noWrap
                  sx={{
                    fontSize: `${finite(typography.baseFontSize, 16) * navbarTitleScale}px`,
                    fontWeight: "var(--wp-segment-title-weight)",
                    letterSpacing: "var(--wp-segment-letter-spacing)",
                    color: navbarDark ? navbarColors.onSecondary : navbarColors.secondary,
                  }}
                >
                  {business.name}
                </Typography>
              ) : null}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ display: desktopLayout ? "flex" : "none", minWidth: 0 }}>
              {navigation.homeLabel ? <Typography data-landing-field="navigation.homeLabel" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.homeLabel}</Typography> : null}
              {footerAboutVisible && navigation.aboutLabel ? <Typography data-landing-field="navigation.aboutLabel" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.aboutLabel}</Typography> : null}
              {footerHighlightsVisible && navigation.highlightsLabel ? <Typography data-landing-field="navigation.highlightsLabel" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.highlightsLabel}</Typography> : null}
              {productsEnabled && navigation.showProductsLink !== false && navigation.productsLabel ? <Typography data-landing-field="navigation.productsLabel" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.productsLabel}</Typography> : null}
              {footerContactVisible && navigation.contactLabel ? <Typography data-landing-field="navigation.contactLabel" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.contactLabel}</Typography> : null}
              {navbarButtons.map((item, index) => (
                <Button
                  data-landing-field={`navigation.buttons.${index}.label`}
                  key={item?.id || `navbar-button-${index}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                    px: 1.2,
                    py: 0.35,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 800,
                    color: navbarDark ? navbarColors.onSecondary : navbarColors.mutedText,
                    borderColor: navbarDark ? "rgba(255,255,255,.28)" : navbarColors.divider,
                    whiteSpace: "nowrap",
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onSelectSection?.("navbar");
                  }}
                >
                  {item.label}
                </Button>
              ))}
              {navigation.showCustomerAccess !== false && navigation.loginLabel ? (
                <Typography data-landing-field="navigation.loginLabel" variant="body2" noWrap sx={{ fontWeight: 800, color: navbarDark ? navbarColors.onSecondary : navbarColors.secondary }}>{navigation.loginLabel}</Typography>
              ) : null}
            </Stack>
            {!desktopLayout ? (
              <IconButton
                size="small"
                aria-label={navigation.menuAriaLabel || "Abrir menú"}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelectSection?.("navbar");
                }}
                sx={{
                  flexShrink: 0,
                  color: navbarDark ? navbarColors.onSecondary : navbarColors.secondary,
                  border: `1px solid ${navbarDark ? "rgba(255,255,255,.2)" : navbarColors.divider}`,
                  bgcolor: navbarDark ? "rgba(255,255,255,.08)" : navbarColors.background,
                }}
              >
                <MenuRoundedIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {segmentAllowed("hero") && sections.showHero !== false && heroHasContent ? (
        <SectionFrame
          sectionId="hero"
          layoutOrder={orderFor("hero")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("hero")}
          sx={{
            ...segmentCssVariables(theme, "hero"),
            ...elementColorSxForSection("hero"),
            minHeight: mobileLayout ? 430 : previewDevice.id === "tablet" ? 480 : 520,
            display: "flex",
            alignItems: heroContentAlign,
            justifyContent: heroContentJustify,
            position: "relative",
            overflow: "hidden",
            bgcolor: heroColors.secondary,
            background: `linear-gradient(135deg, ${heroColors.secondary}, ${heroColors.primary})`,
          }}
        >
          {heroImage ? (
            <SafeCanvasImage
              src={heroImage}
              alt={heroSlide?.alt || heroTitle || business.name || ""}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: heroImagePosition,
                transform: `scale(${heroImageScale})`,
                transformOrigin: "center",
              }}
              {...editableImageProps("hero", heroImageSourcePath, "Editar imagen de portada")}
            />
          ) : null}
          {hero.overlay ? (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                background: hero.overlay,
                pointerEvents: "none",
              }}
            />
          ) : null}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              px: responsive(
                { xs: px(spacing.lg, 24), sm: px(spacing.xxl, 48), md: px(spacing.section, 72) },
                px(spacing.lg, 24)
              ),
              py: responsive(
                {
                  xs: px(finite(spacing.xl, 32) * heroSpacingScale, 32),
                  md: px(finite(spacing.section, 72) * heroSpacingScale, 72),
                },
                px(finite(spacing.xl, 32) * heroSpacingScale, 32)
              ),
              width: "100%",
              maxWidth: 780,
              textAlign: heroTextAlign,
              color: heroColors.onSecondary,
            }}
          >
            {hero.eyebrow ? <Typography data-landing-field="hero.eyebrow" variant="overline" sx={{ opacity: .78, fontWeight: 900 }}>{hero.eyebrow}</Typography> : null}
            {heroTitle ? (
              <Typography
                data-landing-field={heroTitleFieldPath}
                sx={{
                  mt: hero.eyebrow ? .4 : 0,
                  fontFamily: typography.headingFontFamily || "inherit",
                  fontSize: responsive(
                    {
                      xs: `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 1.85 * heroTitleScale}px`,
                      md: `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 3.1 * heroTitleScale}px`,
                    },
                    `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 1.85 * heroTitleScale}px`
                  ),
                  lineHeight: finite(typography.headingLineHeight, 1.15),
                  fontWeight: "var(--wp-segment-title-weight)",
                  letterSpacing: "var(--wp-segment-letter-spacing)",
                  overflowWrap: "anywhere",
                }}
              >
                {heroTitle}
              </Typography>
            ) : null}
            {heroSubtitle ? <Typography data-landing-field={heroSubtitleFieldPath} sx={{ mt: heroTitle || hero.eyebrow ? 1.5 : 0, maxWidth: 650, ml: heroHorizontalPosition === "right" ? "auto" : 0, mr: heroHorizontalPosition === "left" ? "auto" : 0, fontSize: responsive({ xs: ".98rem", md: "1.12rem" }, ".98rem"), lineHeight: 1.65, opacity: .88 }}>{heroSubtitle}</Typography> : null}
            {showHeroPrimaryCta || showHeroSecondaryCta ? (
              <Stack direction={responsive({ xs: "column", sm: "row" }, "column")} spacing={1.2} sx={{ mt: heroTitle || heroSubtitle || hero.eyebrow ? 2.4 : 0, alignItems: responsive({ xs: "stretch", sm: "center" }, "stretch"), justifyContent: responsive({ xs: "stretch", sm: heroContentJustify }, "stretch") }}>
                {showHeroPrimaryCta ? (
                  <Button
                    data-landing-field="hero.primaryCtaLabel"
                    variant="contained"
                    startIcon={hero.primaryCtaShowIcon !== false ? <HeroCanvasCtaIcon name={hero.primaryCtaIcon} fallback="explore" /> : undefined}
                    sx={{ bgcolor: heroColors.primary, color: heroColors.onPrimary, textTransform: "none", boxShadow: shadows.sm }}
                  >
                    {hero.primaryCtaLabel}
                  </Button>
                ) : null}
                {showHeroSecondaryCta ? (
                  <Button
                    data-landing-field="hero.secondaryCtaLabel"
                    variant="outlined"
                    startIcon={hero.secondaryCtaShowIcon !== false ? <HeroCanvasCtaIcon name={hero.secondaryCtaIcon} fallback="storefront" /> : undefined}
                    sx={{ color: heroColors.onSecondary, borderColor: heroColors.onSecondary, textTransform: "none" }}
                  >
                    {hero.secondaryCtaLabel}
                  </Button>
                ) : null}
              </Stack>
            ) : null}
          </Box>
        </SectionFrame>
      ) : null}

      {segmentAllowed("spotlight") && productsEnabled && sections.showProducts !== false && sections.showSpotlight !== false && catalog.spotlight?.enabled && spotlightHasContent ? (
        <SectionFrame
          sectionId="spotlight"
          layoutOrder={orderFor("spotlight")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("spotlight")}
          sx={{
            ...segmentCssVariables(theme, "spotlight"),
            ...elementColorSxForSection("spotlight"),
            position: "relative",
            overflow: "hidden",
            p: responsive({ xs: 2 * spotlightSpacingScale, md: 4 * spotlightSpacingScale }, 2 * spotlightSpacingScale),
            color: "#fff",
            background: spotlightBackground
              ? `linear-gradient(90deg, ${colorWithAlpha((catalog.spotlight.secondaryColor || spotlightColors.secondary), "F2")}, ${colorWithAlpha((catalog.spotlight.primaryColor || spotlightColors.primary), "D9")}), url(${spotlightBackground}) ${spotlightBackgroundPosition}/${Math.round(spotlightImageScale * 100)}% auto no-repeat`
              : `linear-gradient(100deg, ${catalog.spotlight.secondaryColor || spotlightColors.secondary}, ${catalog.spotlight.primaryColor || spotlightColors.primary})`,
          }}
        >
          {studioMode && spotlightBackground ? (
            <Button
              size="small"
              startIcon={<ImageRoundedIcon fontSize="small" />}
              onClick={openImageEditor("spotlight", "catalog.spotlight.backgroundImageUrl")}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 3,
                borderRadius: 999,
                px: 1.15,
                bgcolor: "rgba(25,36,47,.86)",
                color: "#fff",
                textTransform: "none",
                fontWeight: 850,
                boxShadow: "0 8px 24px rgba(0,0,0,.2)",
                "&:hover": { bgcolor: "rgba(25,36,47,.96)" },
              }}
            >
              Editar fondo
            </Button>
          ) : null}
          <Stack direction={responsive({ xs: "column", sm: "row" }, "column")} spacing={2.4} alignItems={responsive({ xs: "stretch", sm: "center" }, "stretch")} sx={{ position: "relative", zIndex: 1 }}>
            {spotlightLogo ? (
              <Box sx={{ width: responsive({ xs: 210 * spotlightImageScale, sm: 250 * spotlightImageScale }, 210 * spotlightImageScale), p: 1.4, bgcolor: "#fff", borderRadius: 2, flexShrink: 0 }}>
                <SafeCanvasImage
                  src={spotlightLogo}
                  alt={catalog.spotlight.logoAlt || ""}
                  sx={{ width: "100%", height: 72 * spotlightImageScale, objectFit: "contain" }}
                  {...editableImageProps("spotlight", "catalog.spotlight.logoUrl", "Editar logo o imagen destacada")}
                />
              </Box>
            ) : null}
            {catalog.spotlight.eyebrow || catalog.spotlight.title || catalog.spotlight.description ? (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {catalog.spotlight.eyebrow ? <Typography data-landing-field="catalog.spotlight.eyebrow" variant="overline" sx={{ opacity: .75 }}>{catalog.spotlight.eyebrow}</Typography> : null}
                {catalog.spotlight.title ? <Typography data-landing-field="catalog.spotlight.title" sx={{ fontSize: responsive({ xs: 30 * spotlightTitleScale, md: 34 * spotlightTitleScale }, 30 * spotlightTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{catalog.spotlight.title}</Typography> : null}
                {catalog.spotlight.description ? <Typography data-landing-field="catalog.spotlight.description" sx={{ mt: catalog.spotlight.title || catalog.spotlight.eyebrow ? .8 : 0, opacity: .82 }}>{catalog.spotlight.description}</Typography> : null}
              </Box>
            ) : null}
            {showSpotlightCta ? (
              <Button data-landing-field="catalog.spotlight.ctaLabel" variant="contained" sx={{ bgcolor: "#fff", color: catalog.spotlight.primaryColor || spotlightColors.primary, textTransform: "none", flexShrink: 0 }}>{catalog.spotlight.ctaLabel}</Button>
            ) : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {segmentAllowed("featured") && productsEnabled && sections.showProducts !== false && sections.showFeatured !== false ? (
        <SectionFrame sectionId="featured" layoutOrder={orderFor("featured")} activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} device={previewDevice.id} backgroundSx={sectionBackgroundSx("featured")} sx={{ ...segmentCssVariables(theme, "featured"), ...elementColorSxForSection("featured"), px: horizontalPadding, py: responsive({ xs: px(finite(spacing.xl, 32) * featuredSpacingScale, 32), md: px(finite(spacing.section, 72) * featuredSpacingScale, 72) }, px(finite(spacing.xl, 32) * featuredSpacingScale, 32)), bgcolor: featuredColors.background }}>
          {catalog.featured?.eyebrow ? <Typography data-landing-field="catalog.featured.eyebrow" variant="overline" sx={{ color: featuredColors.primary, fontWeight: 900 }}>{catalog.featured.eyebrow}</Typography> : null}
          {catalog.featured?.title ? <Typography data-landing-field="catalog.featured.title" sx={{ color: featuredColors.secondary, fontSize: responsive({ xs: 30 * featuredTitleScale, md: 34 * featuredTitleScale }, 30 * featuredTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{catalog.featured.title}</Typography> : null}
          {catalog.featured?.subtitle ? <Typography data-landing-field="catalog.featured.subtitle" sx={{ mt: catalog.featured?.title || catalog.featured?.eyebrow ? .6 : 0, color: featuredColors.mutedText }}>{catalog.featured.subtitle}</Typography> : null}
          <PlaceholderCards labels={["Producto o servicio 1", "Producto o servicio 2", "Producto o servicio 3"]} primary={primary} surface={surface} tokens={tokens} device={previewDevice.id} cardConfig={catalogCards} imageScale={featuredImageScale} spacingScale={featuredSpacingScale} />
        </SectionFrame>
      ) : null}

      {segmentAllowed("brands") && productsEnabled && sections.showProducts !== false && sections.showBrands !== false ? (
        <SectionFrame sectionId="brands" layoutOrder={orderFor("brands")} activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} device={previewDevice.id} backgroundSx={sectionBackgroundSx("brands")} sx={{ ...segmentCssVariables(theme, "brands"), ...elementColorSxForSection("brands"), px: horizontalPadding, py: px(finite(spacing.xl, 32) * brandsSpacingScale, 32), bgcolor: brandsColors.surface }}>
          {catalog.brands?.eyebrow ? <Typography data-landing-field="catalog.brands.eyebrow" variant="overline" sx={{ color: brandsColors.primary, fontWeight: 900 }}>{catalog.brands.eyebrow}</Typography> : null}
          {catalog.brands?.title ? <Typography data-landing-field="catalog.brands.title" sx={{ color: featuredColors.secondary, fontSize: 30 * brandsTitleScale, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{catalog.brands.title}</Typography> : null}
          {catalog.brands?.subtitle ? <Typography data-landing-field="catalog.brands.subtitle" sx={{ mt: catalog.brands?.title || catalog.brands?.eyebrow ? .6 : 0, color: brandsColors.mutedText }}>{catalog.brands.subtitle}</Typography> : null}
          {configuredBrands.length ? <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mt: catalog.brands?.eyebrow || catalog.brands?.title || catalog.brands?.subtitle ? 1.8 : 0 }}>
            {configuredBrands.map(({ item, sourceIndex }, index) => {
              const label = item?.name ?? item?.label ?? "";
              const brandLogo = resolveLandingAssetUrl(item?.logoUrl);
              return (
                <Paper key={`${label}-${sourceIndex >= 0 ? sourceIndex : index}`} elevation={0} sx={{ width: responsive({ xs: 126 * brandsImageScale, sm: 150 * brandsImageScale }, 126 * brandsImageScale), minHeight: 76 * brandsImageScale, px: 1.5, py: 1.2, display: "grid", placeItems: "center", borderRadius: px(borders.radiusMd, 10), bgcolor: brandsColors.background, border: cardBorder, boxShadow: shadows.sm }}>
                  {brandLogo ? (
                    <SafeCanvasImage
                      src={brandLogo}
                      alt={item?.alt || label}
                      sx={{ width: "100%", height: 48 * brandsImageScale, objectFit: "contain" }}
                      {...editableImageProps("brands", `catalog.brands.items.${sourceIndex}.logoUrl`, label ? `Editar logo de ${label}` : "Editar logo de marca")}
                    />
                  ) : (
                    <Typography data-landing-field={`catalog.brands.items.${sourceIndex}.name`} variant="body2" sx={{ textAlign: "center", fontWeight: 850, color: brandsColors.secondary }}>{label}</Typography>
                  )}
                </Paper>
              );
            })}
          </Stack> : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("categories") && productsEnabled && sections.showProducts !== false && sections.showCategories !== false ? (
        <SectionFrame sectionId="categories" layoutOrder={orderFor("categories")} activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} device={previewDevice.id} backgroundSx={sectionBackgroundSx("categories")} sx={{ ...segmentCssVariables(theme, "categories"), ...elementColorSxForSection("categories"), px: horizontalPadding, py: px(finite(spacing.xl, 32) * categoriesSpacingScale, 32), bgcolor: categoriesColors.surface }}>
          {catalog.categories?.eyebrow ? <Typography data-landing-field="catalog.categories.eyebrow" variant="overline" sx={{ color: categoriesColors.primary, fontWeight: 900 }}>{catalog.categories.eyebrow}</Typography> : null}
          {catalog.categories?.title ? <Typography data-landing-field="catalog.categories.title" sx={{ color: categoriesColors.secondary, fontSize: 30 * categoriesTitleScale, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{catalog.categories.title}</Typography> : null}
          {catalog.categories?.subtitle ? <Typography data-landing-field="catalog.categories.subtitle" sx={{ mt: catalog.categories?.title || catalog.categories?.eyebrow ? .6 : 0, color: categoriesColors.mutedText }}>{catalog.categories.subtitle}</Typography> : null}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: catalog.categories?.title || catalog.categories?.eyebrow || catalog.categories?.subtitle ? 1.5 : 0 }} aria-hidden>
            {[0, 1, 2].map((item) => <Box key={item} sx={{ width: 92, height: 32, borderRadius: 999, bgcolor: colorWithAlpha(primary, "18"), border: cardBorder }} />)}
          </Stack>
        </SectionFrame>
      ) : null}

      {segmentAllowed("about") && sections.showAbout !== false && aboutHasContent ? (
        <SectionFrame
          sectionId="about"
          layoutOrder={orderFor("about")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("about")}
          sx={{
            ...segmentCssVariables(theme, "about"),
            ...elementColorSxForSection("about"),
            display: "grid",
            gridTemplateColumns: responsive({ xs: "1fr", md: aboutImage && aboutHasCopy ? (about.layout === "image-right" ? "1.1fr .9fr" : ".9fr 1.1fr") : "1fr" }, "1fr"),
            bgcolor: aboutAppearance.backgroundColor || aboutColors.background,
          }}
        >
          {aboutImage ? (
          <Box
            role={studioMode && aboutImage ? "button" : undefined}
            tabIndex={studioMode && aboutImage ? 0 : undefined}
            title={studioMode && aboutImage ? "Editar imagen del negocio" : undefined}
            aria-label={studioMode && aboutImage ? "Editar imagen del negocio" : undefined}
            onClick={studioMode && aboutImage ? openImageEditor("about", "about.imageUrl") : undefined}
            onKeyDown={studioMode && aboutImage ? (event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              openImageEditor("about", "about.imageUrl")(event);
            } : undefined}
            sx={{
              minHeight: responsive({ xs: 220 * aboutImageScale, md: 390 * aboutImageScale }, 220 * aboutImageScale),
              order: about.layout === "image-right" && desktopLayout ? 2 : undefined,
              bgcolor: colorWithAlpha(primary, "18"),
              backgroundImage: aboutImage ? `url(${aboutImage})` : "none",
              backgroundSize: "cover",
              backgroundPosition: normalizeImagePosition(about.imagePosition),
              ...(studioMode && aboutImage ? {
                cursor: "pointer",
                outline: "2px solid transparent",
                outlineOffset: -2,
                transition: "outline-color .15s ease, filter .15s ease",
                "&:hover": { outlineColor: "rgba(243,90,58,.92)", filter: "brightness(.96)" },
                "&:focus-visible": { outlineColor: "rgba(243,90,58,1)" },
              } : {}),
            }}
          />
          ) : null}
          {aboutHasCopy ? (
            <Box
              sx={{
                p: responsive({ xs: px(finite(spacing.xl, 32) * aboutSpacingScale, 32), md: px(finite(spacing.section, 72) * aboutSpacingScale, 72) }, px(finite(spacing.xl, 32) * aboutSpacingScale, 32)),
                alignSelf: "center",
                order: about.layout === "image-right" && aboutImage && desktopLayout ? 1 : undefined,
              }}
            >
              {about.eyebrow ? <Typography data-landing-field="about.eyebrow" variant="overline" sx={{ color: aboutAppearance.accentColor || aboutColors.primary, fontWeight: 900 }}>{about.eyebrow}</Typography> : null}
              {about.title ? <Typography data-landing-field="about.title" sx={{ color: aboutAppearance.titleColor || aboutColors.secondary, fontSize: responsive({ xs: 30 * aboutTitleScale, md: 34 * aboutTitleScale }, 30 * aboutTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{about.title}</Typography> : null}
              {about.description ? <Typography data-landing-field="about.description" sx={{ mt: about.title || about.eyebrow ? 1.2 : 0, color: aboutAppearance.textColor || aboutColors.mutedText, lineHeight: 1.7, whiteSpace: "pre-line" }}>{about.description}</Typography> : null}
              {showAboutCta ? <Button data-landing-field="about.ctaLabel" variant="contained" sx={{ mt: about.title || about.eyebrow || about.description ? 2.2 : 0, bgcolor: aboutAppearance.accentColor || aboutColors.primary, color: aboutColors.onPrimary, textTransform: "none", boxShadow: shadows.sm }}>{about.ctaLabel}</Button> : null}
            </Box>
          ) : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("highlights") && sections.showHighlights !== false && highlightsHasContent ? (
        <SectionFrame
          sectionId="highlights"
          layoutOrder={orderFor("highlights")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("highlights")}
          sx={{
            ...segmentCssVariables(theme, "highlights"),
            ...elementColorSxForSection("highlights"),
            position: "relative",
            overflow: "hidden",
            px: horizontalPadding,
            py: responsive(
              { xs: px(finite(spacing.xl, 32) * highlightsSpacingScale, 32), md: px(finite(spacing.section, 72) * highlightsSpacingScale, 72) },
              px(finite(spacing.xl, 32) * highlightsSpacingScale, 32)
            ),
            bgcolor: highlightsAppearance.backgroundColor || highlightsColors.surface,
          }}
        >
          {highlightsBackground ? (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${highlightsBackground})`,
                backgroundSize: "cover",
                backgroundPosition: highlightsBackgroundPosition,
                backgroundRepeat: "no-repeat",
                opacity: highlightsBackgroundOpacity,
                pointerEvents: "none",
              }}
            />
          ) : null}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            {highlightsIntro.eyebrow ? <Typography data-landing-field="highlightsIntro.eyebrow" variant="overline" sx={{ color: highlightsAppearance.accentColor || highlightsColors.primary, fontWeight: 900 }}>{highlightsIntro.eyebrow}</Typography> : null}
            {highlightsIntro.title ? <Typography data-landing-field="highlightsIntro.title" sx={{ color: highlightsAppearance.titleColor || highlightsColors.secondary, fontSize: responsive({ xs: 30 * highlightsTitleScale, md: 34 * highlightsTitleScale }, 30 * highlightsTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{highlightsIntro.title}</Typography> : null}
            {highlightsIntro.subtitle ? <Typography data-landing-field="highlightsIntro.subtitle" sx={{ mt: highlightsIntro.title || highlightsIntro.eyebrow ? .7 : 0, color: highlightsAppearance.textColor || highlightsColors.mutedText }}>{highlightsIntro.subtitle}</Typography> : null}
            {visibleHighlights.length ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: responsive(
                    {
                      xs: "minmax(0, 1fr)",
                      sm: visibleHighlights.length === 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
                      md: `repeat(${Math.max(1, Math.min(visibleHighlights.length, 4))}, minmax(0, 1fr))`,
                    },
                    "minmax(0, 1fr)"
                  ),
                  gap: responsive({ xs: 1.4, sm: 1.6, md: 2 }, 1.4),
                  mt: highlightsIntro.eyebrow || highlightsIntro.title || highlightsIntro.subtitle ? 2 : 0,
                  alignItems: "stretch",
                }}
              >
                {visibleHighlights.map((item, index) => {
                  const highlightImage = resolveLandingAssetUrl(item.imageUrl);
                  const highlightVideo = youtubeEmbedUrl(item.youtubeUrl);
                  const cardSurface = highlightsAppearance.surfaceColor || highlightsColors.background;
                  return (
                    <Paper
                      key={item.id || index}
                      elevation={0}
                      sx={{
                        minWidth: 0,
                        height: "100%",
                        overflow: "hidden",
                        borderRadius: px(borders.radiusMd, 10),
                        bgcolor: cardSurface,
                        border: cardBorder,
                        boxShadow: shadows.sm,
                      }}
                    >
                      {highlightVideo ? (
                        <Box
                          component="iframe"
                          src={highlightVideo}
                          title={item.title || `Video del diferencial ${index + 1}`}
                          sx={{ width: "100%", aspectRatio: "16 / 9", display: "block", border: 0, bgcolor: "#000", pointerEvents: studioMode ? "none" : "auto" }}
                        />
                      ) : highlightImage ? (
                        <Box sx={{ position: "relative" }}>
                          <SafeCanvasImage
                            src={highlightImage}
                            alt={item.imageAlt || item.title || ""}
                            sx={{ width: "100%", height: 150 * highlightsImageScale, objectFit: "cover", objectPosition: normalizeImagePosition(item.imagePosition) }}
                            {...editableImageProps("highlights", `highlights.${index}.imageUrl`, `Editar imagen de ${item.title || `diferencial ${index + 1}`}`)}
                          />
                          {item.icon ? (
                            <Box
                              sx={{
                                position: "absolute",
                                left: px(finite(spacing.md, 16), 16),
                                bottom: -20,
                                width: 42,
                                height: 42,
                                borderRadius: px(borders.radiusSm, 5),
                                display: "grid",
                                placeItems: "center",
                                color: highlightsAppearance.accentColor || highlightsColors.primary,
                                bgcolor: cardSurface,
                                border: cardBorder,
                                boxShadow: shadows.sm,
                              }}
                            >
                              <HighlightCanvasIcon name={item.icon} sx={{ fontSize: 24 }} />
                            </Box>
                          ) : null}
                        </Box>
                      ) : item.icon ? (
                        <Box sx={{ px: px(finite(spacing.lg, 24) * highlightsSpacingScale, 24), pt: px(finite(spacing.lg, 24) * highlightsSpacingScale, 24) }}>
                          <Box
                            sx={{
                              width: 46,
                              height: 46,
                              borderRadius: px(borders.radiusSm, 5),
                              display: "grid",
                              placeItems: "center",
                              color: highlightsAppearance.accentColor || highlightsColors.primary,
                              bgcolor: colorWithAlpha(primary, "12"),
                              border: cardBorder,
                            }}
                          >
                            <HighlightCanvasIcon name={item.icon} sx={{ fontSize: 26 }} />
                          </Box>
                        </Box>
                      ) : null}
                      <Box sx={{
                        p: px(finite(spacing.lg, 24) * highlightsSpacingScale, 24),
                        pt: highlightImage && item.icon
                          ? px(finite(spacing.lg, 24) * highlightsSpacingScale + 8, 32)
                          : (highlightImage || highlightVideo)
                            ? px(finite(spacing.lg, 24) * highlightsSpacingScale, 24)
                            : item.icon
                              ? px(finite(spacing.md, 16) * highlightsSpacingScale, 16)
                              : px(finite(spacing.lg, 24) * highlightsSpacingScale, 24),
                      }}>
                        {item.title ? <Typography data-landing-field={`highlights.${index}.title`} sx={{ color: highlightsAppearance.titleColor || highlightsColors.secondary, fontWeight: 950 }}>{item.title}</Typography> : null}
                        {item.description ? <Typography data-landing-field={`highlights.${index}.description`} variant="body2" sx={{ mt: item.title ? .6 : 0, color: highlightsAppearance.textColor || highlightsColors.mutedText, lineHeight: 1.55 }}>{item.description}</Typography> : null}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            ) : null}
          </Box>
        </SectionFrame>
      ) : null}

      {segmentAllowed("custom") && (custom.eyebrow || custom.title || custom.description || custom.imageUrl || custom.youtubeUrl || custom.calendlyUrl || (custom.ctaLabel && custom.ctaUrl)) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "custom"}
          sectionType="custom"
          sectionLabel={custom.title || "Nueva sección"}
          layoutOrder={orderFor("custom")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("custom")}
          sx={{
            ...segmentCssVariables(theme, "custom"),
            ...elementColorSxForSection("custom"),
            px: horizontalPadding,
            py: responsive({ xs: px(finite(spacing.xl, 32), 32), md: px(finite(spacing.section, 72), 72) }, px(finite(spacing.xl, 32), 32)),
            bgcolor: customColors.background,
          }}
        >
          {(() => {
            const customImage = resolveLandingAssetUrl(custom.imageUrl);
            const customVideo = youtubeEmbedUrl(custom.youtubeUrl);
            const customCalendly = normalizeCalendlyUrl(custom.calendlyUrl);
            const customHasMedia = Boolean(customVideo || customCalendly || customImage);
            const customCentered = custom.layout === "centered";
            const customContentLeft = custom.layout === "image-right";
            const customContentRight = !customCentered && !customContentLeft;
            const media = customHasMedia ? (
              <Stack spacing={2} sx={{ width: "100%" }}>
                {customCalendly ? (
                  <Box
                    component="iframe"
                    src={customCalendly}
                    title={custom.title ? `Calendly - ${custom.title}` : "Calendly"}
                    sx={{ width: "100%", height: responsive({ xs: 500, md: 620 }, 500), border: 0, borderRadius: px(borders.radiusMd, 10), bgcolor: "#fff", pointerEvents: "none" }}
                  />
                ) : null}
                {customVideo ? (
                  <Box component="iframe" src={customVideo} title={custom.title || "Video"} sx={{ width: "100%", aspectRatio: "16 / 9", border: 0, borderRadius: px(borders.radiusMd, 10), bgcolor: "#000", pointerEvents: "none" }} />
                ) : null}
                {customImage ? (
                  <SafeCanvasImage
                    src={customImage}
                    alt={custom.imageAlt || custom.title || ""}
                    sx={{ width: "100%", height: responsive({ xs: 230, md: 330 }, 230), objectFit: "cover", objectPosition: normalizeImagePosition(custom.imagePosition), borderRadius: px(borders.radiusMd, 10) }}
                  />
                ) : null}
              </Stack>
            ) : null;
            return (
              <Box sx={{ display: "grid", gridTemplateColumns: customCentered ? "minmax(0,1fr)" : responsive({ xs: "minmax(0,1fr)", md: "minmax(0,1fr) minmax(0,1fr)" }, "minmax(0,1fr)"), gap: responsive({ xs: 2, md: 4 }, 2), alignItems: "center" }}>
                {customHasMedia && !customCentered && customContentRight ? media : null}
                <Stack
                  spacing={1.1}
                  sx={{
                    gridColumn: customCentered ? "1" : responsive({ xs: "1", md: customContentRight ? "2" : "1" }, "1"),
                    maxWidth: customCentered ? 760 : 620,
                    width: "100%",
                    mx: customCentered ? "auto" : 0,
                    justifySelf: customCentered ? "center" : customContentRight ? "end" : "start",
                    textAlign: customCentered ? "center" : customContentRight ? "right" : "left",
                  }}
                >
                  {custom.eyebrow ? <Typography data-landing-field="custom.eyebrow" variant="overline" sx={{ color: customColors.primary, fontWeight: 900 }}>{custom.eyebrow}</Typography> : null}
                  {custom.title ? <Typography data-landing-field="custom.title" sx={{ color: featuredColors.secondary, fontSize: responsive({ xs: 30, md: 38 }, 30), fontWeight: 950 }}>{custom.title}</Typography> : null}
                  {custom.description ? <Typography data-landing-field="custom.description" sx={{ color: customColors.mutedText, whiteSpace: "pre-line", lineHeight: 1.65 }}>{custom.description}</Typography> : null}
                  {custom.ctaLabel && custom.ctaUrl ? <Button data-landing-field="custom.ctaLabel" variant="contained" sx={{ alignSelf: customCentered ? "center" : customContentRight ? "flex-end" : "flex-start", textTransform: "none" }}>{custom.ctaLabel}</Button> : null}
                </Stack>
                {customHasMedia && !customCentered && customContentLeft ? media : null}
                {customHasMedia && customCentered ? <Box>{media}</Box> : null}
              </Box>
            );
          })()}
        </SectionFrame>
      ) : null}

      {segmentAllowed("contact") && sections.showContact !== false && contactHasContent ? (
        <SectionFrame
          sectionId="contact"
          layoutOrder={orderFor("contact")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("contact")}
          sx={{
            ...segmentCssVariables(theme, "contact"),
            ...elementColorSxForSection("contact"),
            px: horizontalPadding,
            py: responsive({ xs: px(finite(spacing.xl, 32) * contactSpacingScale, 32), md: px(finite(spacing.section, 72) * contactSpacingScale, 72) }, px(finite(spacing.xl, 32) * contactSpacingScale, 32)),
            color: contactAppearance.textColor || contactColors.onSecondary,
            backgroundColor: contactAppearance.backgroundColor || contactColors.secondary,
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: responsive({ xs: "1fr", md: contactImage ? "1.35fr .65fr" : "1fr" }, "1fr"), gap: responsive({ xs: 2, md: 3 }, 2), alignItems: "stretch" }}>
            <Box sx={{ minWidth: 0, alignSelf: "center" }}>
              {contact.title ? <Typography data-landing-field="contact.title" sx={{ color: contactAppearance.titleColor || "inherit", fontSize: responsive({ xs: 30 * contactTitleScale, md: 34 * contactTitleScale }, 30 * contactTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{contact.title}</Typography> : null}
              {contact.subtitle ? <Typography data-landing-field="contact.subtitle" sx={{ mt: contact.title ? .8 : 0, opacity: .78 }}>{contact.subtitle}</Typography> : null}
              {contactHasActions ? (
                <Stack direction={responsive({ xs: "column", sm: "row" }, "column")} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mt: contact.title || contact.subtitle ? 2 : 0 }}>
                  {whatsappDigits && contact.whatsappCtaLabel ? <Button data-landing-field="contact.whatsappCtaLabel" variant="contained" startIcon={<WhatsAppIcon />} sx={{ bgcolor: contactAppearance.accentColor || contactColors.primary, color: contactColors.onPrimary, textTransform: "none", boxShadow: shadows.sm }}>{contact.whatsappCtaLabel}</Button> : null}
                  {contact.detailsCtaLabel && contact.detailsCtaUrl ? <Button data-landing-field="contact.detailsCtaLabel" variant="outlined" sx={{ color: contactAppearance.titleColor || contactColors.onSecondary, borderColor: "rgba(255,255,255,.7)", textTransform: "none" }}>{contact.detailsCtaLabel}</Button> : null}
                  {businessAddress ? <Chip label={businessAddress} sx={{ maxWidth: "100%", bgcolor: "rgba(255,255,255,.1)", color: "#fff", "& .MuiChip-label": { overflowWrap: "anywhere" } }} /> : null}
                  {business.schedule ? <Chip label={business.schedule} sx={{ maxWidth: "100%", bgcolor: "rgba(255,255,255,.1)", color: "#fff", "& .MuiChip-label": { overflowWrap: "anywhere" } }} /> : null}
                  {business.email ? <Chip label={business.email} sx={{ maxWidth: "100%", bgcolor: "rgba(255,255,255,.1)", color: "#fff", "& .MuiChip-label": { overflowWrap: "anywhere" } }} /> : null}
                  {business.phone ? <Chip label={business.phone} sx={{ maxWidth: "100%", bgcolor: "rgba(255,255,255,.1)", color: "#fff" }} /> : null}
                </Stack>
              ) : null}
            </Box>
            {contactImage ? (
              <SafeCanvasImage
                src={contactImage}
                alt={contact.mapTitle || ""}
                sx={{ width: "100%", minHeight: responsive({ xs: 220 * contactImageScale, md: 300 * contactImageScale }, 220 * contactImageScale), maxHeight: 430 * contactImageScale, height: "100%", objectFit: "cover", objectPosition: contactImagePosition, borderRadius: px(borders.radiusLg, 18), border: "1px solid rgba(255,255,255,.18)", boxShadow: shadows.md }}
                {...editableImageProps("contact", "contact.backgroundImageUrl", "Editar imagen de contacto")}
              />
            ) : null}
          </Box>
        </SectionFrame>
      ) : null}

      {segmentAllowed("footer") && sections.showFooter !== false && footerHasVisibleContent ? (
        <SectionFrame
          sectionId="footer"
          layoutOrder={orderFor("footer")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("footer")}
          sx={{
            ...segmentCssVariables(theme, "footer"),
            ...elementColorSxForSection("footer"),
            px: horizontalPadding,
            py: px(finite(spacing.lg, 24) * footerSpacingScale, 24),
            bgcolor: footerStyle === "style-2" ? footerColors.surface : footerStyle === "style-3" ? "#10171e" : footerColors.secondary,
            color: footerStyle === "style-2" ? footerColors.text : footerColors.onSecondary,
            borderTop: footerStyle === "style-2" ? cardBorder : "none",
          }}
        >
          <Stack spacing={2} alignItems={footerStyle === "style-3" ? "center" : "stretch"} textAlign={footerStyle === "style-3" ? "center" : "left"}>
            <Stack direction={footerStyle === "style-3" ? "column" : responsive({ xs: "column", md: "row" }, "column")} justifyContent="space-between" alignItems={footerStyle === "style-3" ? "center" : "flex-start"} spacing={3} sx={{ width: "100%" }}>
              {footerHasBrand ? <Stack direction={footerStyle === "style-3" ? "column" : "row"} alignItems={footerStyle === "style-3" ? "center" : "flex-start"} spacing={1.1} sx={{ minWidth: 0, flex: "1 1 260px" }}>
                {footer.showLogo !== false && logo ? (
                  <SafeCanvasImage
                    src={logo}
                    alt=""
                    sx={{ width: 42 * footerImageScale, height: 42 * footerImageScale, objectFit: "contain", objectPosition: footerLogoPosition, flexShrink: 0 }}
                    {...editableImageProps("footer", logoSourcePath, "Editar logo del pie de página")}
                  />
                ) : null}
                {business.name || footer.tagline ? <Box sx={{ minWidth: 0 }}>
                  {business.name ? <Typography data-landing-field="business.name" noWrap sx={{ fontSize: `${finite(typography.baseFontSize, 16) * footerTitleScale}px`, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{business.name}</Typography> : null}
                  {footer.tagline ? <Typography data-landing-field="footer.tagline" variant="caption" sx={{ display: "block", mt: business.name ? 0.35 : 0, opacity: .72, whiteSpace: "pre-line" }}>{footer.tagline}</Typography> : null}
                </Box> : null}
              </Stack> : null}

              {footerHasNavigation ? <Box sx={{ flex: "1 1 280px", minWidth: 0 }}>
                {footer.navigationTitle ? <Typography data-landing-field="footer.navigationTitle" variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{footer.navigationTitle}</Typography> : null}
                <Stack direction="row" spacing={1.4} useFlexGap flexWrap="wrap" justifyContent={footerStyle === "style-3" ? "center" : "flex-start"}>
                  {footer.homeLabel ? <Typography data-landing-field="footer.homeLabel" variant="caption">{footer.homeLabel}</Typography> : null}
                  {productsEnabled && navigation.showProductsLink !== false && footer.productsLabel ? <Typography data-landing-field="footer.productsLabel" variant="caption">{footer.productsLabel}</Typography> : null}
                  {footerAboutVisible && navigation.aboutLabel ? <Typography data-landing-field="navigation.aboutLabel" variant="caption">{navigation.aboutLabel}</Typography> : null}
                  {footerHighlightsVisible && navigation.highlightsLabel ? <Typography data-landing-field="navigation.highlightsLabel" variant="caption">{navigation.highlightsLabel}</Typography> : null}
                  {footerContactVisible && footer.contactLabel ? <Typography data-landing-field="footer.contactLabel" variant="caption">{footer.contactLabel}</Typography> : null}
                </Stack>
              </Box> : null}

              {footerHasContact ? <Box sx={{ flex: "0 1 220px", minWidth: 0 }}>
                {footer.contactTitle ? <Typography data-landing-field="footer.contactTitle" variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{footer.contactTitle}</Typography> : null}
                {businessAddress ? <Typography data-landing-field="business.address" variant="caption" sx={{ display: "block", opacity: .72, overflowWrap: "anywhere" }}>{businessAddress}</Typography> : null}
                {business.schedule ? <Typography data-landing-field="business.schedule" variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72, overflowWrap: "anywhere" }}>{business.schedule}</Typography> : null}
                {business.email ? <Typography data-landing-field="business.email" variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72, overflowWrap: "anywhere" }}>{business.email}</Typography> : null}
                {business.phone ? <Typography data-landing-field="business.phone" variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72 }}>{business.phone}</Typography> : null}
              </Box> : null}

              {footerSocialLabels.length ? <Box sx={{ flex: "0 1 220px", minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{navigation.socialTitle || "Redes sociales"}</Typography>
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" justifyContent={footerStyle === "style-3" ? "center" : "flex-start"}>
                  {footerSocialLabels.map((label) => <Chip key={label} size="small" label={label} sx={{ maxWidth: "100%", bgcolor: footerStyle === "style-2" ? background : "rgba(255,255,255,.1)", color: "inherit" }} />)}
                </Stack>
              </Box> : null}
            </Stack>

            {footerCopyright ? (
              <Typography data-landing-field="footer.copyright" variant="caption" sx={{ width: "100%", pt: 1.2, borderTop: footerStyle === "style-2" ? `1px solid ${footerColors.divider}` : "1px solid rgba(255,255,255,.12)", opacity: .65 }}>
                {footerCopyright}
              </Typography>
            ) : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {!bare && !onlySection ? layoutSegments
        .filter((segment) => segment.id !== segment.type && segment.visible !== false)
        .map((segment) => (
          <LandingCanvas
            key={segment.id}
            form={applyLandingSegmentSnapshot(form, segment)}
            activeSection={studioMode && segment.type === "custom" ? activeSection : ""}
            device={device}
            productsEnabled={productsEnabled}
            studioMode={studioMode && segment.type === "custom"}
            onSelectSection={segment.type === "custom" ? onSelectSection : undefined}
            onOpenSegmentTool={segment.type === "custom" ? onOpenSegmentTool : undefined}
            onToggleSection={segment.type === "custom" ? onToggleSection : undefined}
            onlySection={segment.type}
            bare
            layoutInstanceId={segment.id}
          />
        )) : null}

      {!bare && !onlySection && business.whatsapp && sections.showFloatingWhatsapp !== false ? (
        <Box
          role="button"
          tabIndex={0}
          aria-label={contact.floatingWhatsappAriaLabel || "Contactar por WhatsApp"}
          onClick={() => onSelectSection?.("floatingWhatsapp")}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") onSelectSection?.("floatingWhatsapp");
          }}
          sx={{
            position: "absolute",
            right: 18,
            bottom: 18,
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "#25D366",
            color: "#fff",
            cursor: studioMode ? "pointer" : "default",
            boxShadow: activeSection === "floatingWhatsapp" ? "0 0 0 4px rgba(243,90,58,.45), 0 8px 24px rgba(0,0,0,.25)" : "0 8px 24px rgba(0,0,0,.25)",
            zIndex: 4,
          }}
        >
          <WhatsAppIcon />
        </Box>
      ) : null}
    </Box>
  );
}
